import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { nanoid } from 'nanoid';
import analyticsService from './services/analyticsService';
import { NotFoundPage } from './pages/NotFound';
import Welcome from './pages/Welcome';
import AIChatbot from './pages/AIChatbot';
import OperatorChatInterface from './pages/OperatorChatbot';
import ErrorToast from './components/ErrorToast';
import LoadingOverlay from './components/LoadingOverlay';
import ErrorTest from './pages/ErrorTest';
import FeedbackOverlay from './components/FeedbackOverlay';
import { extractErrorCode, setErrorHandler } from './utils/errorHandler';
import { useLanguage, useTranslate } from './lib/i18n';
import {
  useCreateSession,
  useCreateAuthSession,
  useConfig,
  useWidgetLandingPageRedirect,
} from './hooks';
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import IntakeTool from './pages/IntakeTool';
import EnvironmentSign from './components/EnvironmentSign';
import { config } from './lib/config';
import ChatbotLayout from './layouts/ChatbotLayout';
import TrainingToolLayout from './layouts/TrainingToolLayout';
import IntakeToolLayout from './layouts/IntakeToolLayout';
import EmbeddedModeLayout from './layouts/EmbeddedModeLayout';
import { EmbeddedModeProvider, useIsEmbeddedMode } from './context/EmbeddedModeContext';
import { useWidgetCommunication } from './hooks/useWidgetCommunication';
import { getHigherContrastColor, mixWithWhite } from './utils/colorUtils';
import { useConfigId } from './context/ConfigContext';
import InformationModal from './components/InformationModal';
import { useFavicons } from './hooks/useFavicon';

const WidgetLandingPageRedirectHandler = () => {
  useWidgetLandingPageRedirect();
  return null;
};

const AppContent = () => {
  const isEmbeddedMode = useIsEmbeddedMode();

  const {
    mutateAsync: createSession,
    isPending: isLoading,
    isSuccess: sessionCreated,
  } = useCreateSession();
  const {
    mutateAsync: createAuthSession,
    isPending: isAuthLoading,
    isSuccess: authSessionCreated,
  } = useCreateAuthSession();
  const [topic, setTopic] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showFeedbackOverlay, setShowFeedbackOverlay] = useState<boolean>(false);
  const [infoModalCloseTime, setInfoModalCloseTime] = useState<number | null>(null);
  const { language } = useLanguage();
  const t = useTranslate();
  const { configId } = useConfigId();
  const favicons = useFavicons();

  const showErrorWithTimeout = useCallback(
    (err: unknown, duration = 5000) => {
      const errorCode = extractErrorCode(err);
      const errorMessage = t(`common.errors.${errorCode}`);

      // Set the error state to display in UI
      setError(errorMessage);

      // Clear the error after specified duration
      setTimeout(() => setError(null), duration);
    },
    [t]
  );

  // Initialize Sentry user identification
  useEffect(() => {
    // Generate a new unique user ID for Sentry on each page load
    // No persistent storage in cookies or localStorage
    const anonymousId = nanoid();
    Sentry.setUser({ id: anonymousId });

    if (configId && !isEmbeddedMode) {
      analyticsService.init(configId);
      analyticsService.trackEvent({
        eventName: 'appLoaded',
      });
    }
  }, [configId, isEmbeddedMode]);

  useEffect(() => {
    if (!isEmbeddedMode || !configId) return;

    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (typeof data !== 'object' || data === null) return;

      if (data.version === 1 && data.event === 'open') {
        analyticsService.init(configId);
        analyticsService.trackEvent({
          eventName: 'clickWidgetOpenButton',
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isEmbeddedMode, configId]);

  const { data: chatbotConfig, isLoading: isConfigLoading, error: configError } = useConfig();
  const isInfoModalEnabled = !!chatbotConfig && (chatbotConfig.displayInformationModal ?? true);

  // Set up favicon based on chatbot config
  useEffect(() => {
    if (!favicons) return;

    // Add favicon link tags
    favicons.forEach(({ size, url, rel }) => {
      if (!url) return;

      const link = document.createElement('link');
      link.rel = rel;
      link.sizes = `${size}x${size}`;
      link.href = url;
      link.type = 'image/png';

      document.head.appendChild(link!);
    });

    // Cleanup function
    return () => {
      const links = document.querySelectorAll('link[rel*="icon"]');
      links.forEach((link) => link.remove());
    };
  }, [favicons]);

  // Apply chatbot primary and secondary colors
  useEffect(() => {
    if (!chatbotConfig) return;

    const root = document.documentElement;
    const { primaryColor, secondaryColor } = chatbotConfig;

    // Set the primary and secondary colors
    root.style.setProperty('--primary', primaryColor);
    root.style.setProperty('--secondary', secondaryColor);
    root.style.setProperty('--secondary-light', mixWithWhite(secondaryColor, 0.2));
    root.style.setProperty(
      '--secondary-light-foreground',
      getHigherContrastColor(mixWithWhite(secondaryColor, 0.2))
    );

    // Calculate and set the best foreground colors for contrast
    const primaryForeground = getHigherContrastColor(primaryColor);
    const secondaryForeground = getHigherContrastColor(secondaryColor);

    root.style.setProperty('--primary-foreground', primaryForeground);
    root.style.setProperty('--secondary-foreground', secondaryForeground);

    return () => {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--secondary');
      root.style.removeProperty('--primary-foreground');
      root.style.removeProperty('--secondary-foreground');
    };
  }, [chatbotConfig?.primaryColor, chatbotConfig?.secondaryColor]);

  // Apply Sekasin typography exception
  useEffect(() => {
    if (!chatbotConfig) return;

    const root = document.documentElement;
    const isSekasin = chatbotConfig.title === 'Sekasin';

    if (isSekasin) {
      root.style.setProperty('--font-heading', "'Radikal Heading', sans-serif");
      root.style.setProperty('--font-black', "'Radikal Bold', sans-serif");
      root.style.setProperty('--font-body', "'Radikal Body', sans-serif");
      root.style.setProperty('--font-button', "'Radikal Button', sans-serif");
    }

    return () => {
      if (isSekasin) {
        root.style.removeProperty('--font-heading');
        root.style.removeProperty('--font-black');
        root.style.removeProperty('--font-body');
        root.style.removeProperty('--font-button');
      }
    };
  }, [chatbotConfig?.title]);

  // Apply chatbot theme mode
  useEffect(() => {
    const root = document.documentElement;

    if (!chatbotConfig) return;

    root.setAttribute('data-theme-mode', chatbotConfig.themeMode);

    return () => {
      root.removeAttribute('data-theme-mode');
    };
  }, [chatbotConfig?.themeMode]);

  useEffect(() => {
    if (!chatbotConfig || !isEmbeddedMode) return;

    window.parent.postMessage(
      {
        version: 1,
        event: 'config',
        config: {
          widgetButtonEnabled: chatbotConfig.widgetButtonEnabled,
          widgetButtonColor: chatbotConfig.widgetButtonColor,
          widgetButtonText: chatbotConfig.widgetButtonText,
          widgetButtonBorderRadius: chatbotConfig.widgetButtonBorderRadius,
          widgetButtonPosition: chatbotConfig.widgetButtonPosition,
        },
      },
      '*'
    );
  }, [chatbotConfig, isEmbeddedMode]);

  // Initialize the error handler
  useEffect(() => {
    setErrorHandler((error: Error) => {
      // Don't show error toast for network errors (they're shown inline in forms)
      const errorCode = extractErrorCode(error);
      if (errorCode === 'network_error') return;
      showErrorWithTimeout(error);
    });
  }, [showErrorWithTimeout]);

  useEffect(() => {
    // Skip session creation for /intake route - it will create sessions on user action
    if (location.pathname.startsWith('/intake')) return;

    // Only proceed if session hasn't been created yet and we're not already loading
    if (authSessionCreated || isLoading || isAuthLoading || !chatbotConfig) return;

    const initAuthSession = async () => {
      try {
        await createAuthSession({
          chatbotConfigId: chatbotConfig.id,
        });
      } catch (error) {
        console.error('Failed to create auth session:', error);
        showErrorWithTimeout(error);
      }
    };

    initAuthSession();
  }, [
    authSessionCreated,
    isLoading,
    isAuthLoading,
    chatbotConfig,
    createAuthSession,
    showErrorWithTimeout,
    location.pathname,
  ]);

  useEffect(() => {
    // Skip session creation for /intake route - it will create sessions on user action
    if (location.pathname.startsWith('/intake')) return;

    // Only proceed if session hasn't been created yet and we're not already loading
    if (sessionCreated || isLoading || !authSessionCreated) return;

    // Create an async function inside the effect
    const initiateSession = async () => {
      try {
        await createSession({
          gender: 'other',
          consent: true,
          language,
        });
      } catch (error) {
        // Handle the error
        console.error('Failed to create session:', error);

        // Show error message for 5 seconds then clear it
        showErrorWithTimeout(error);
      }
    };

    // Call the async function
    initiateSession();
  }, [
    sessionCreated,
    isLoading,
    authSessionCreated,
    language,
    createSession,
    showErrorWithTimeout,
    location.pathname,
  ]);

  const handleWidgetClose = useCallback(() => {
    console.log('Requesting feedback', showFeedbackOverlay);
    if (!isEmbeddedMode) {
      // Legacy widget communication
      setShowFeedbackOverlay(true);
      window.parent.postMessage({ type: 'widget:feedbackModalOpen' }, '*');
    } else {
      window.parent.postMessage({ version: 1, event: 'close' }, '*');
    }
  }, [isEmbeddedMode, showFeedbackOverlay]);

  const handleWidgetHide = useCallback(() => {
    window.parent.postMessage({ version: 1, event: 'close' }, '*');
  }, []);

  const handleFeedbackSubmitted = useCallback(() => {
    setShowFeedbackOverlay(false);
    if (!isEmbeddedMode) {
      window.parent.postMessage('widget:closeWithFeedback', '*');
    } else {
      window.parent.postMessage({ version: 1, event: 'close' }, '*');
    }
  }, [isEmbeddedMode]);

  // Initialize widget communication
  const { showInformationModal, handleInformationModalClose } = useWidgetCommunication({
    onInfoModalClose: setInfoModalCloseTime,
    onFeedbackRequest: handleWidgetClose,
    infoModalCloseTime,
    isInfoModalEnabled,
  });

  const pathname = window.location.pathname;
  const isIntakeRoute = pathname.startsWith('/intake');
  const isTrainingLoginRoute = pathname.startsWith('/training/login');
  const requiresSession = !isIntakeRoute && !isTrainingLoginRoute;
  const showLoadingOverlay =
    !configError &&
    (isLoading || isConfigLoading || !chatbotConfig || (requiresSession && !sessionCreated));

  return (
    <EmbeddedModeLayout
      isFeedbackOpen={showFeedbackOverlay}
      widgetCloseInteraction={handleWidgetClose}
      widgetHideInteraction={handleWidgetHide}
    >
      <Router>
        <>
          <WidgetLandingPageRedirectHandler />
          <EnvironmentSign />
          <InformationModal
            showInformationModal={showInformationModal && isInfoModalEnabled}
            onClose={handleInformationModalClose}
          />

          {showLoadingOverlay && <LoadingOverlay />}

          {configError ? (
            <div className="flex items-center justify-center min-h-full">
              <p className="text-lg">{t('common.errors.config_not_found')}</p>
            </div>
          ) : (
            <>
              <div className="bg-background overflow-x-hidden h-full px-4">
                {showFeedbackOverlay && (
                  <FeedbackOverlay
                    setShowFeedbackOverlay={setShowFeedbackOverlay}
                    onFeedbackSubmitted={handleFeedbackSubmitted}
                  />
                )}

                <Routes>
                  <Route path="/training" element={<TrainingToolLayout />}>
                    <Route
                      index
                      element={
                        sessionCreated ? (
                          <ProtectedRoute>
                            <OperatorChatInterface topic={topic} setTopic={setTopic} />
                          </ProtectedRoute>
                        ) : null
                      }
                    />
                    <Route path="login" element={<Login />} />
                  </Route>

                  <Route path="/" element={<ChatbotLayout />}>
                    <Route
                      index
                      element={
                        chatbotConfig && sessionCreated ? (
                          <>
                            <title>{`Jonotusapuri | ${chatbotConfig.title}`}</title>
                            <Welcome topic={topic} setTopic={setTopic} />
                          </>
                        ) : null
                      }
                    />
                    <Route
                      path="ai-chatbot"
                      element={
                        chatbotConfig && sessionCreated ? (
                          <>
                            <title>{`Tekoälybotti | ${chatbotConfig.title}`}</title>
                            <AIChatbot
                              topic={topic}
                              setTopic={setTopic}
                              onRequestFeedback={handleWidgetClose}
                              chatbotConfig={chatbotConfig}
                            />
                          </>
                        ) : null
                      }
                    />
                    {config.isDevelopment && <Route path="error" element={<ErrorTest />} />}
                  </Route>

                  <Route path="/intake" element={<IntakeToolLayout />}>
                    <Route index element={<IntakeTool />} />
                  </Route>

                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </div>
            </>
          )}

          {error && <ErrorToast error={error} onClose={() => setError(null)} />}
        </>
      </Router>
    </EmbeddedModeLayout>
  );
};

const App = () => {
  // Redirect old /operator routes to /training and fallback to Sekasin ID when none defined
  const path = window.location.pathname;
  const search = window.location.search || '?config=e4b3b3b5-62e5-4457-a707-459d3ba41c1d';
  if (path == '/operator' || path == '/operator/chat') {
    window.location.replace(`/training${search}`);
  }
  if (path == '/operator/login') {
    window.location.replace(`/training/login${search}`);
  }

  return (
    <EmbeddedModeProvider>
      <AppContent />
    </EmbeddedModeProvider>
  );
};

export default App;
